import React, { useState, useEffect } from 'react';
import { getHrisKPI, getHrisKPIById, createHrisKPI, updateHrisKPI, deleteHrisKPI, getKaryawan } from '../../api';
import { Plus, Edit2, Trash2, BarChart3, Search, Award, Target, CheckCircle } from 'lucide-react';

const ASPEK_KPI = ['Kedisiplinan', 'Kualitas Kerja', 'Teamwork', 'Inisiatif', 'Penyelesaian Tugas', 'Pelayanan', 'Kehadiran', 'Kompetensi'];
const STATUS_KPI = ['Draft', 'Dinilai', 'Dikonfirmasi'];

const statusStyle = {
  'Draft': { bg: '#fef3c7', color: '#92400e', dot: '#fb6340' },
  'Dinilai': { bg: '#eef9fd', color: '#0a66c2', dot: '#11cdef' },
  'Dikonfirmasi': { bg: '#d1fae5', color: '#065f46', dot: '#2dce89' },
};

export default function HrisKPI() {
  const [data, setData] = useState([]);
  const [karyawan, setKaryawan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterPeriode, setFilterPeriode] = useState(new Date().toISOString().slice(0, 7));
  const [filterAspek, setFilterAspek] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ karyawan_id: '', periode: new Date().toISOString().slice(0, 7), aspek: 'Kedisiplinan', target: '', skor: 80, bobot: 20, catatan: '' });

  const fetchData = () => {
    setLoading(true);
    Promise.all([getHrisKPI({ periode: filterPeriode }), getKaryawan()])
      .then(([dRes, kRes]) => { setData(dRes.data); setKaryawan(kRes.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [filterPeriode]);

  const openCreate = () => {
    setEditing(null);
    setForm({ karyawan_id: '', periode: filterPeriode, aspek: 'Kedisiplinan', target: '', skor: 80, bobot: 20, catatan: '' });
    setShowModal(true);
  };

  const openEdit = async (id) => {
    try {
      const res = await getHrisKPIById(id);
      const d = res.data;
      setEditing(d);
      setForm({ karyawan_id: d.karyawan_id, periode: d.periode, aspek: d.aspek, target: d.target,
        realisasi: d.realisasi, skor: d.skor, bobot: d.bobot, status: d.status, catatan: d.catatan });
      setShowModal(true);
    } catch (e) { alert('Gagal memuat data'); }
  };

  const handleSubmit = async () => {
    if (!form.karyawan_id || !form.periode || !form.aspek) { alert('Lengkapi field wajib'); return; }
    try {
      if (editing) {
        await updateHrisKPI(editing.id, { ...form, status: form.status || 'Dinilai' });
      } else {
        await createHrisKPI(form);
      }
      setShowModal(false);
      fetchData();
    } catch (e) { alert('Gagal menyimpan'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus penilaian KPI ini?')) return;
    try { await deleteHrisKPI(id); fetchData(); } catch (e) { alert('Gagal menghapus'); }
  };

  const filtered = data.filter(d => {
    if (search && !d.nama?.toLowerCase().includes(search.toLowerCase()) && !d.aspek?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterAspek && d.aspek !== filterAspek) return false;
    return true;
  });

  // Group KPI by karyawan for aggregate
  const grouped = {};
  filtered.forEach(d => {
    if (!grouped[d.karyawan_id]) grouped[d.karyawan_id] = { nama: d.nama, items: [], totalNilai: 0, totalBobot: 0, avgSkor: 0, count: 0 };
    grouped[d.karyawan_id].items.push(d);
    grouped[d.karyawan_id].totalNilai += d.nilai_akhir;
    grouped[d.karyawan_id].totalBobot += d.bobot;
    grouped[d.karyawan_id].avgSkor += d.skor;
    grouped[d.karyawan_id].count++;
  });
  Object.values(grouped).forEach(g => { g.avgSkor = g.avgSkor / g.count; });

  if (loading) return <div className="page-loading"><div className="spinner-lg" /></div>;

  return (
    <div className="page-container">
      <div className="page-header" style={{ borderBottom: '2px solid var(--gray-100)', paddingBottom: 16, marginBottom: 20 }}>
        <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--gray-600)' }}>
          <BarChart3 size={22} style={{ marginRight: 8, verticalAlign: 'middle', color: 'var(--cyan-500)' }} />
          KPI / Penilaian Kinerja
        </h2>
      </div>

      {/* Toolbar: Tambah | Search | Filter Aspek | Periode */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
        padding: '12px 16px', background: '#fff', borderRadius: 12,
        boxShadow: '0 0 2rem 0 rgba(136,152,170,0.1)',
        flexWrap: 'wrap',
      }}>
        <button className="btn btn-primary" onClick={openCreate} style={{ whiteSpace: 'nowrap' }}>
          <Plus size={16} /> Tambah Penilaian
        </button>
        <div style={{ flex: 1, minWidth: 200, maxWidth: 320 }}>
          <div className="search-box" style={{ width: '100%', margin: 0 }}>
            <Search size={16} />
            <input placeholder="Cari berdasarkan nama atau aspek..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          <label style={{ fontSize: '0.78rem', color: 'var(--gray-400)', fontWeight: 500, whiteSpace: 'nowrap' }}>Aspek:</label>
          <select value={filterAspek} onChange={e => setFilterAspek(e.target.value)}
            className="form-select"
            style={{ width: 'auto', padding: '7px 12px', borderRadius: 8, border: '1px solid var(--gray-300)', fontSize: '0.82rem' }}>
            <option value="">Semua</option>
            {ASPEK_KPI.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <label style={{ fontSize: '0.78rem', color: 'var(--gray-400)', fontWeight: 500, whiteSpace: 'nowrap' }}>Periode:</label>
          <input type="month" value={filterPeriode} onChange={e => setFilterPeriode(e.target.value)}
            className="form-select"
            style={{ width: 140, padding: '7px 12px', borderRadius: 8, border: '1px solid var(--gray-300)', fontSize: '0.82rem' }} />
          <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>
            <strong style={{ color: 'var(--gray-600)' }}>{filtered.length}</strong>/{data.length}
          </span>
        </div>
      </div>

      {/* Summary Cards per Karyawan */}
      {Object.keys(grouped).length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginBottom: 24 }}>
          {Object.values(grouped).sort((a, b) => b.totalNilai - a.totalNilai).map(g => {
            const rataNilai = g.totalBobot > 0 ? (g.totalNilai / g.totalBobot * 100) : 0;
            const borderColor = rataNilai >= 80 ? '#2dce89' : rataNilai >= 60 ? '#fb6340' : '#f5365c';
            return (
              <div key={g.nama} style={{
                background: '#fff', borderRadius: 12, padding: 16,
                boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)',
                borderLeft: `4px solid ${borderColor}`
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: rataNilai >= 80 ? 'rgba(45,206,137,0.15)' : rataNilai >= 60 ? 'rgba(251,99,64,0.15)' : 'rgba(245,54,92,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <Award size={18} color={rataNilai >= 80 ? '#2dce89' : rataNilai >= 60 ? '#fb6340' : '#f5365c'} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--gray-600)' }}>{g.nama}</div>
                    <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{g.count} aspek dinilai</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16 }}>
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: borderColor }}>
                      {rataNilai.toFixed(0)}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Nilai</div>
                  </div>
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--gray-600)' }}>{g.avgSkor.toFixed(0)}</div>
                    <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Rata Skor</div>
                  </div>
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--gray-600)' }}>{g.totalBobot}%</div>
                    <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Total Bobot</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="page-loading">Tidak ada data KPI</div>
      ) : (
        <div className="regions-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))' }}>
          {filtered.map(d => {
            const nilaiAkhir = (d.skor * d.bobot / 100);
            const ss = statusStyle[d.status] || statusStyle['Draft'];
            return (
              <div key={d.id} className="region-card" style={{ borderTop: `3px solid ${ss.dot}` }}>
                <div className="region-card-header" style={{
                  background: '#fff', borderBottom: '1px solid var(--gray-100)',
                  padding: '14px 16px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: 'linear-gradient(135deg, var(--cyan-500), var(--cyan-700))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                    }}>
                      <BarChart3 size={20} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <h3 style={{ fontSize: '0.95rem', margin: 0, color: 'var(--gray-600)' }}>{d.nama}</h3>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
                          background: ss.bg, color: ss.color,
                        }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: ss.dot }} />
                          {d.status}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: 2 }}>
                        {d.aspek} · {d.periode}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="region-card-body" style={{ padding: '12px 16px' }}>
                  <div className="region-detail"><Target size={14} className="text-muted" /><strong>Target:</strong> {d.target || <span style={{ color: 'var(--gray-400)', fontStyle: 'italic' }}>—</span>}</div>
                  {d.realisasi && <div className="region-detail"><CheckCircle size={14} className="text-muted" /><strong>Realisasi:</strong> {d.realisasi}</div>}
                  <div className="region-detail" style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 8, marginTop: 8 }}>
                    <div style={{ display: 'flex', gap: 16, width: '100%' }}>
                      <div style={{ textAlign: 'center', flex: 1, background: '#f8f9fe', borderRadius: 8, padding: '8px 12px' }}>
                        <div style={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--gray-400)', marginBottom: 2 }}>Skor</div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--gray-600)' }}>{d.skor}</div>
                      </div>
                      <div style={{ textAlign: 'center', flex: 1, background: '#f8f9fe', borderRadius: 8, padding: '8px 12px' }}>
                        <div style={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--gray-400)', marginBottom: 2 }}>Bobot</div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--gray-600)' }}>{d.bobot}%</div>
                      </div>
                      <div style={{ textAlign: 'center', flex: 1, background: 'var(--accent-tint-bg)', borderRadius: 8, padding: '8px 12px', border: '1px solid var(--cyan-100)' }}>
                        <div style={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--cyan-700)', marginBottom: 2 }}>Nilai</div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--cyan-700)' }}>{nilaiAkhir.toFixed(1)}</div>
                      </div>
                    </div>
                  </div>
                  {d.catatan && (
                    <div className="region-detail" style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 8, marginTop: 8 }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)', fontStyle: 'italic' }}>{d.catatan}</span>
                    </div>
                  )}
                </div>
                <div className="action-btns" style={{ padding: '10px 16px', borderTop: '1px solid var(--gray-100)', justifyContent: 'flex-end' }}>
                  <button className="btn-icon" title="Edit" onClick={() => openEdit(d.id)}><Edit2 size={14} /></button>
                  <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(d.id)}><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, var(--cyan-500) 0%, var(--cyan-700) 100%)',
              margin: -1, padding: '20px 24px', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
              display: 'flex', alignItems: 'center', gap: 12
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <BarChart3 size={18} color="white" />
              </div>
              <h3 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>{editing ? 'Edit KPI' : 'Tambah KPI'}</h3>
            </div>
            <div className="modal-body">
              <div style={{
                background: 'var(--accent-tint-bg)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 16,
                border: '1px solid var(--cyan-100)'
              }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Karyawan</label>
                    <select value={form.karyawan_id} onChange={e => setForm({...form, karyawan_id: parseInt(e.target.value)})} required>
                      <option value="">Pilih Karyawan</option>
                      {karyawan.map(k => <option key={k.id} value={k.id}>{k.nama}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Periode</label>
                    <input type="month" value={form.periode} onChange={e => setForm({...form, periode: e.target.value})} required />
                  </div>
                </div>
                <div className="form-group">
                  <label>Aspek Penilaian</label>
                  <select value={form.aspek} onChange={e => setForm({...form, aspek: e.target.value})}>
                    {ASPEK_KPI.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Target</label>
                  <input type="text" value={form.target} onChange={e => setForm({...form, target: e.target.value})} placeholder="Contoh: 100% kehadiran" />
                </div>
                {editing && (
                  <div className="form-group">
                    <label>Realisasi</label>
                    <input type="text" value={form.realisasi || ''} onChange={e => setForm({...form, realisasi: e.target.value})} placeholder="Realisasi aktual" />
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label>Skor (0-100)</label>
                    <input type="number" min={0} max={100} value={form.skor} onChange={e => setForm({...form, skor: parseInt(e.target.value) || 0})} />
                  </div>
                  <div className="form-group">
                    <label>Bobot (0-100)%</label>
                    <input type="number" min={0} max={100} value={form.bobot} onChange={e => setForm({...form, bobot: parseInt(e.target.value) || 0})} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Nilai Akhir: <strong>{((form.skor * form.bobot) / 100).toFixed(1)}</strong></label>
                </div>
                <div className="form-group">
                  <label>Catatan</label>
                  <textarea value={form.catatan} onChange={e => setForm({...form, catatan: e.target.value})} rows={2} />
                </div>
                {editing && (
                  <div className="form-group">
                    <label>Status</label>
                    <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                      {STATUS_KPI.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSubmit}>{editing ? 'Simpan' : 'Tambah'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
